/**
 * T-0559: one read for every caller that asks for the same thing at about the same time.
 *
 * Several mounted hooks read the same preference on their own: the toolbar order was read twice and
 * the work-area order three times on one cold launch, against the budget's *no read sent twice*. A
 * caller gets the read already in flight for its key, or an answer that arrived within
 * `FRESH_FOR_MS`; anything older is asked for again, so a later refresh still reaches the server.
 * A write forgets its key first, so no caller is handed what was stored before it.
 */
export const FRESH_FOR_MS = 2_000;

interface ISharedEntry {
  read: Promise<unknown>;
  /** When the answer arrived; `null` while it is in flight. */
  answeredAt: number | null;
}

const entries = new Map<string, ISharedEntry>();

export function sharedRead<T>(
  key: string,
  read: () => Promise<T>,
  now: () => number = Date.now,
): Promise<T> {
  const entry = entries.get(key);
  if (entry && (entry.answeredAt === null || now() - entry.answeredAt < FRESH_FOR_MS)) {
    return entry.read as Promise<T>;
  }
  const started: ISharedEntry = { read: read(), answeredAt: null };
  entries.set(key, started);
  started.read.then(
    () => {
      if (entries.get(key) === started) started.answeredAt = now();
    },
    () => {
      // A failed read is not shared: the next caller asks again.
      if (entries.get(key) === started) entries.delete(key);
    },
  );
  return started.read as Promise<T>;
}

/** Drops what is shared for `key`, before a write changes what the server holds. */
export function forgetSharedRead(key: string): void {
  entries.delete(key);
}

export function resetSharedReadsForTests(): void {
  entries.clear();
}
